import { QueryFunction, useQuery } from 'react-query';
import {
    DailyItemsByDate,
    DataItem,
    getActivities,
    GetActivitiesQueries,
    getDailyItemsWithQuantityByDate,
    getSiteReport,
    getUsersMissingReports,
    getValidationSitesSummary,
    MissingReportsResponse,
    SiteReport,
    SiteReportQueries,
    ValidationSiteSummary,
    ValidationSiteSummaryQueries
} from '../../services/apiSLTP/dailyReports/activities';
import { User } from '../../services/apiSLTP/users';

type UseActivitiesQueryOptions = {
    queries?: GetActivitiesQueries;
};

const getActivitiesQuery: QueryFunction<DataItem[]> = async ({ queryKey }) => {
    const [, options] = queryKey as [string, UseActivitiesQueryOptions];
    const { queries } = options;

    const response = await getActivities(queries);
    return response.data;
};

export const useActivitiesQuery = (options: UseActivitiesQueryOptions) => {
    return useQuery(['activities', options], getActivitiesQuery);
};


// -- VALIDATION QUERIES --
type UseValidationSitesSummaryOptions = {
    queries?: ValidationSiteSummaryQueries;
};

const fetchValidationSitesSummary: QueryFunction<ValidationSiteSummary[]> = async ({ queryKey }) => {
    const [, options] = queryKey as [string, UseValidationSitesSummaryOptions];
    const { queries } = options;

    const response = await getValidationSitesSummary(queries);
    return response.data;
};

export const useValidationSitesSummary = (options: UseValidationSitesSummaryOptions) => {
    return useQuery(['validationSitesSummary', options], fetchValidationSitesSummary, {
        enabled: !options.queries || !!options.queries.date,
    });
};

type UseSiteReportQueryOptions = {
    siteId: string;
    stepMode: number;
    queries: SiteReportQueries;
};

const fetchSiteReport: QueryFunction<SiteReport> = async ({ queryKey }) => {
    const [, options] = queryKey as [string, UseSiteReportQueryOptions];
    const { siteId, stepMode, queries } = options;

    const response = await getSiteReport(siteId, stepMode, queries);
    return response.data;
};

export const useSiteReportQuery = (options: UseSiteReportQueryOptions) => {
    return useQuery(['siteReport', options], fetchSiteReport, {
        enabled: !!options.siteId && !!options.queries.date,
    });
};

type UseUsersMissingReportsOptions = {
    siteId: string;
    date: string;
};

const fetchUsersMissingReports: QueryFunction<User[]> = async ({ queryKey }) => {
    const [, options] = queryKey as [string, UseUsersMissingReportsOptions];
    const { siteId, date } = options;

    const response: MissingReportsResponse = await getUsersMissingReports(siteId, { date });
    return response.data;
};

export const useUsersMissingReportsQuery = (options: UseUsersMissingReportsOptions) => {
    return useQuery(['usersMissingReports', options], fetchUsersMissingReports, {
        enabled: !!options.siteId && !!options.date,
    });
};

type UseDailyItemsByDateOptions = {
    date: string;
};

const fetchDailyItemsByDate: QueryFunction<DailyItemsByDate> = async ({ queryKey }) => {
    const [, options] = queryKey as [string, UseDailyItemsByDateOptions];
    const { date } = options;

    const response = await getDailyItemsWithQuantityByDate(date);
    return response.data;
};

export const useDailyItemsByDateQuery = (options: UseDailyItemsByDateOptions) => {
    return useQuery(['dailyItemsByDate', options], fetchDailyItemsByDate, {
        enabled: !!options.date,
    });
};
